<template>
  <div class="dist-form-page">
    <div class="form-hero">
      <p class="form-crumb">
        Distribuidores / {{ form.id ? 'Editar' : 'Nuevo' }}
      </p>
      <h1 class="form-title">
        {{ form.id ? 'Editar Distribuidor' : 'Nuevo Distribuidor' }}
      </h1>
      <p class="form-subtitle">
        {{
          form.id
            ? 'Actualiza la información del distribuidor y sus laboratorios asociados.'
            : 'Completa la información del distribuidor para registrarlo en el sistema.'
        }}
      </p>
    </div>

    <div class="form-card">
      <v-form @submit.prevent="submit">
        <!-- ===== Información General ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Información General</h3>
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label"
                >Nombre del Distribuidor
                <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.name"
                v-validate="'required'"
                data-vv-name="name"
                :error-messages="errors.collect('name')"
                class="form-input"
                placeholder="Ingrese el nombre del distribuidor"
                solo
                hide-details
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label"
                >Número de Teléfono <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.phone"
                v-validate="'required'"
                data-vv-name="phone"
                :error-messages="errors.collect('phone')"
                class="form-input"
                placeholder="Ingrese el número de teléfono"
                solo
                hide-details
                append-icon="phone"
              ></v-text-field>
            </div>
            <div class="form-field form-field--full">
              <label class="form-label"
                >Dirección Exacta <span class="form-required">*</span></label
              >
              <v-textarea
                v-model="form.address"
                v-validate="'required'"
                data-vv-name="address"
                :error-messages="errors.collect('address')"
                class="form-input"
                placeholder="Ingrese la dirección completa del distribuidor"
                solo
                hide-details
                append-icon="place"
                rows="3"
                auto-grow
              ></v-textarea>
              <p class="form-hint">
                Incluya calle, edificio, piso, oficina, corregimiento, ciudad y
                país.
              </p>
            </div>

            <div class="form-field form-field--full">
              <label class="form-label">
                Ubicación en el mapa
                <span class="form-label-opt"
                  >(arrastra el marcador o busca)</span
                >
              </label>
              <div class="map-search-wrap">
                <v-icon small class="map-search-wrap__icon">search</v-icon>
                <gmap-autocomplete
                  class="map-search-input"
                  placeholder="Buscar dirección o lugar en el mapa..."
                  :options="{
                    fields: ['geometry', 'formatted_address', 'name'],
                  }"
                  @place_changed="onPlaceChanged"
                ></gmap-autocomplete>
              </div>
              <gmap-map
                ref="gmap"
                class="dist-map"
                :center="mapCenter"
                :zoom="mapZoom"
                :options="{
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: true,
                  gestureHandling: 'greedy',
                }"
                @click="onMapClick"
              >
                <gmap-marker
                  v-if="hasLocation"
                  :position="markerPosition"
                  :draggable="true"
                  @dragend="onMarkerDragEnd"
                ></gmap-marker>
              </gmap-map>
              <p class="form-hint map-coords">
                <template v-if="hasLocation">
                  <v-icon x-small class="form-hint__icon">place</v-icon>
                  Ubicación fijada: {{ Number(form.latitude).toFixed(6) }},
                  {{ Number(form.longitude).toFixed(6) }}
                  <a class="map-clear" @click="clearLocation">Quitar</a>
                </template>
                <template v-else>
                  Busca una dirección o arrastra el marcador para fijar la
                  ubicación exacta del distribuidor.
                </template>
              </p>
            </div>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Notificaciones ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Notificaciones</h3>
          <div class="form-toggle-row">
            <v-switch v-model="enableEmails" class="form-switch"></v-switch>
            <div class="form-toggle-text">
              <p class="form-toggle-label">
                Habilitar correos para notificaciones
              </p>
              <p class="form-toggle-help">
                Active esta opción si desea asignar correos para recibir
                notificaciones del sistema.
              </p>
            </div>
          </div>
          <div v-if="enableEmails" class="form-field" style="margin-top:14px">
            <label class="form-label">
              Emails para Notificaciones
              <v-icon small class="form-hint__icon">help_outline</v-icon>
            </label>
            <v-combobox
              v-model="form.emails"
              class="form-input"
              chips
              multiple
              solo
              hide-details
              placeholder="Ingrese los correos electrónicos separados por comas"
              append-icon="email"
            ></v-combobox>
            <p class="form-hint">
              Ejemplo: notificaciones@distribuidor.com, compras@distribuidor.com
            </p>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Laboratorios Asociados ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Laboratorios Asociados</h3>
          <div class="form-field">
            <label class="form-label"
              >Laboratorios <span class="form-required">*</span></label
            >
            <v-select
              v-model="form.laboratories"
              v-validate="'required'"
              data-vv-name="laboratories"
              :error-messages="errors.collect('laboratories')"
              :items="laboratories"
              item-text="name"
              item-value="id"
              class="form-input"
              chips
              multiple
              solo
              hide-details
              no-data-text="Datos no disponibles"
              placeholder="Seleccione uno o más laboratorios"
            ></v-select>
            <p class="form-hint">
              Seleccione los laboratorios con los que trabajará este
              distribuidor.
            </p>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Persona de Contacto ===== -->
        <section class="form-section">
          <h3 class="form-section__title">
            Persona Principal de Contacto
            <span class="form-section__badge">(Administrador)</span>
          </h3>
          <p class="form-section__help">
            Este es el usuario del sistema vinculado al distribuidor. Puede
            editar su información y restablecer la contraseña.
          </p>
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label"
                >Nombre <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.contact.firstName"
                v-validate="'required'"
                data-vv-name="firstName"
                :error-messages="errors.collect('firstName')"
                class="form-input"
                placeholder="Ingrese el nombre"
                solo
                hide-details
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label">Apellido</label>
              <v-text-field
                v-model="form.contact.lastName"
                data-vv-name="lastName"
                :error-messages="errors.collect('lastName')"
                class="form-input"
                placeholder="Ingrese el apellido"
                solo
                hide-details
              ></v-text-field>
            </div>
            <div class="form-field" :class="{ 'form-field--full': form.id }">
              <label class="form-label"
                >Email <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.contact.email"
                v-validate="'required|email'"
                data-vv-name="email"
                :error-messages="errors.collect('email')"
                class="form-input"
                placeholder="Ingrese el correo electrónico"
                solo
                hide-details
                type="email"
                append-icon="email"
              ></v-text-field>
            </div>
            <!-- Create mode: password + confirm -->
            <div class="form-field" v-if="!form.id">
              <label class="form-label"
                >Contraseña <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.contact.password"
                v-validate="'required'"
                data-vv-name="password"
                :error-messages="errors.collect('password')"
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
                class="form-input"
                placeholder="Ingrese la contraseña"
                solo
                hide-details
              ></v-text-field>
            </div>
            <div class="form-field" v-if="!form.id">
              <label class="form-label"
                >Confirmar Contraseña
                <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                :append-icon="
                  showConfirmPassword ? 'visibility' : 'visibility_off'
                "
                @click:append="showConfirmPassword = !showConfirmPassword"
                class="form-input"
                placeholder="Confirme la contraseña"
                solo
                hide-details
              ></v-text-field>
              <p v-if="passwordErrorText" class="form-error-text">
                {{ passwordErrorText }}
              </p>
            </div>
          </div>
          <!-- Edit mode: reset por defecto -->
          <div v-if="form.id" class="form-action-row">
            <div class="form-action-row__text">
              <p class="form-action-row__title">
                Reset con contraseña por defecto
              </p>
              <p class="form-action-row__help">
                Restablece la contraseña del usuario vinculado con el valor por
                defecto definido por el sistema.
              </p>
            </div>
            <v-btn class="btn-secondary" small @click="openResetPasswordDialog">
              <v-icon left small>lock_reset</v-icon>Reset por defecto
            </v-btn>
          </div>
        </section>

        <div v-if="form.id" class="form-divider"></div>

        <section v-if="form.id" class="form-section">
          <div class="additional-users__header">
            <div>
              <h3 class="form-section__title additional-users__title">
                Usuarios Adicionales
              </h3>
              <p class="form-section__help additional-users__help">
                Agregue los usuarios adicionales que tendrán acceso al sistema
                bajo este distribuidor.
              </p>
            </div>
            <v-btn
              class="additional-users__btn"
              @click="openAdditionalUserDialog"
            >
              <v-icon left small>add</v-icon>Agregar Usuario
            </v-btn>
          </div>

          <div class="additional-users__table-wrap">
            <table
              class="additional-users__table"
              cellspacing="0"
              cellpadding="0"
            >
              <thead>
                <tr>
                  <th>Nombre Completo</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody v-if="form.additionalUsers.length">
                <tr
                  v-for="(user, index) in form.additionalUsers"
                  :key="user.id || user.email || index"
                >
                  <td>{{ getAdditionalUserName(user) }}</td>
                  <td>{{ user.email }}</td>
                  <td>{{ getAdditionalUserRole(user) }}</td>
                  <td>
                    <v-btn
                      icon
                      small
                      class="additional-users__edit"
                      @click="openAdditionalUserDialog(user, index)"
                    >
                      <v-icon small>edit</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      class="additional-users__remove"
                      @click="removeAdditionalUser(user, index)"
                    >
                      <v-icon small>delete_outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              v-if="form.additionalUsers.length"
              class="additional-users__cards"
            >
              <div
                v-for="(user, index) in form.additionalUsers"
                :key="`mobile-${user.id || user.email || index}`"
                class="additional-users__card"
              >
                <div class="additional-users__card-row">
                  <span class="additional-users__card-label"
                    >Nombre Completo</span
                  >
                  <span class="additional-users__card-value">{{
                    getAdditionalUserName(user)
                  }}</span>
                </div>
                <div class="additional-users__card-row">
                  <span class="additional-users__card-label">Email</span>
                  <span class="additional-users__card-value">{{
                    user.email
                  }}</span>
                </div>
                <div class="additional-users__card-row">
                  <span class="additional-users__card-label">Rol</span>
                  <span class="additional-users__card-value">{{
                    getAdditionalUserRole(user)
                  }}</span>
                </div>
                <div class="additional-users__card-actions">
                  <v-btn
                    small
                    text
                    class="additional-users__edit-text"
                    @click="openAdditionalUserDialog(user, index)"
                  >
                    <v-icon left small>edit</v-icon>Editar
                  </v-btn>
                  <v-btn
                    small
                    text
                    class="additional-users__remove-text"
                    @click="removeAdditionalUser(user, index)"
                  >
                    <v-icon left small>delete_outline</v-icon>Eliminar
                  </v-btn>
                </div>
              </div>
            </div>

            <div
              v-if="!form.additionalUsers.length"
              class="additional-users__empty"
            >
              <v-icon class="additional-users__empty-icon">group_add</v-icon>
              <div>
                <p class="additional-users__empty-title">
                  No hay usuarios adicionales agregados.
                </p>
                <p class="additional-users__empty-text">
                  Haga clic en "Agregar Usuario" para añadir contactos.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- ===== Footer ===== -->
        <div class="form-footer">
          <v-btn
            class="btn-cancel"
            flat
            @click="$router.push('/distribuidores/listado')"
          >
            <v-icon left small>close</v-icon>Cancelar
          </v-btn>
          <v-btn type="submit" class="btn-save" :loading="saving">
            <v-icon left small>save</v-icon>
            {{ form.id ? 'Guardar Cambios' : 'Guardar Distribuidor' }}
          </v-btn>
        </div>
      </v-form>
    </div>

    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <message :options="dialog" v-on:close="dialog.show = false"></message>
    </v-dialog>

    <v-dialog max-width="640" persistent v-model="additionalUserDialog.show">
      <v-card class="modal-card">
        <div class="modal-header">
          <h3 class="modal-header__title">
            {{
              additionalUserDialog.mode === 'edit'
                ? 'Editar Usuario Adicional'
                : 'Agregar Usuario Adicional'
            }}
          </h3>
          <p class="modal-header__subtitle">
            Este usuario tendrá acceso al sistema bajo el distribuidor actual.
          </p>
        </div>
        <v-card-text class="modal-body">
          <div class="form-grid form-grid--modal">
            <div class="form-field">
              <label class="form-label"
                >Nombre <span class="form-required">*</span></label
              >
              <input
                v-model="additionalUserDialog.form.firstName"
                class="native-form-input"
                placeholder="Ingrese el nombre"
                type="text"
              />
              <p
                v-if="additionalUserDialog.errors.firstName"
                class="form-error-text"
              >
                {{ additionalUserDialog.errors.firstName }}
              </p>
            </div>

            <div class="form-field">
              <label class="form-label"
                >Apellido <span class="form-required">*</span></label
              >
              <input
                v-model="additionalUserDialog.form.lastName"
                class="native-form-input"
                placeholder="Ingrese el apellido"
                type="text"
              />
              <p
                v-if="additionalUserDialog.errors.lastName"
                class="form-error-text"
              >
                {{ additionalUserDialog.errors.lastName }}
              </p>
            </div>

            <div class="form-field">
              <label class="form-label"
                >Email <span class="form-required">*</span></label
              >
              <input
                v-model="additionalUserDialog.form.email"
                class="native-form-input"
                placeholder="usuario@dominio.com"
                type="email"
              />
              <p
                v-if="additionalUserDialog.errors.email"
                class="form-error-text"
              >
                {{ additionalUserDialog.errors.email }}
              </p>
            </div>

            <div class="form-field">
              <label class="form-label">Rol</label>
              <input
                class="native-form-input native-form-input--readonly"
                type="text"
                value="Distribuidor"
                readonly
              />
            </div>

            <div class="form-field">
              <label class="form-label">
                Contraseña
                <span
                  v-if="additionalUserDialog.mode !== 'edit'"
                  class="form-required"
                  >*</span
                >
                <span v-else class="form-label-opt">(opcional)</span>
              </label>
              <div class="native-form-input native-form-input--password">
                <input
                  v-model="additionalUserDialog.form.password"
                  :type="showAdditionalPassword ? 'text' : 'password'"
                  class="native-form-input__control"
                  placeholder="Ingrese la contraseña"
                />
                <button
                  type="button"
                  class="native-form-input__toggle"
                  @click="showAdditionalPassword = !showAdditionalPassword"
                >
                  <v-icon small>{{
                    showAdditionalPassword ? 'visibility' : 'visibility_off'
                  }}</v-icon>
                </button>
              </div>
              <p
                v-if="additionalUserDialog.errors.password"
                class="form-error-text"
              >
                {{ additionalUserDialog.errors.password }}
              </p>
            </div>

            <div class="form-field">
              <label class="form-label">
                Confirmar Contraseña
                <span
                  v-if="additionalUserDialog.mode !== 'edit'"
                  class="form-required"
                  >*</span
                >
              </label>
              <div class="native-form-input native-form-input--password">
                <input
                  v-model="additionalUserDialog.form.confirmPassword"
                  :type="showAdditionalConfirmPassword ? 'text' : 'password'"
                  class="native-form-input__control"
                  placeholder="Confirme la contraseña"
                />
                <button
                  type="button"
                  class="native-form-input__toggle"
                  @click="
                    showAdditionalConfirmPassword = !showAdditionalConfirmPassword
                  "
                >
                  <v-icon small>{{
                    showAdditionalConfirmPassword
                      ? 'visibility'
                      : 'visibility_off'
                  }}</v-icon>
                </button>
              </div>
              <p
                v-if="additionalUserDialog.errors.confirmPassword"
                class="form-error-text"
              >
                {{ additionalUserDialog.errors.confirmPassword }}
              </p>
            </div>
          </div>
        </v-card-text>
        <div class="modal-footer">
          <v-btn
            class="btn-cancel"
            flat
            small
            @click="closeAdditionalUserDialog"
            >Cancelar</v-btn
          >
          <v-btn
            class="btn-save"
            small
            :loading="additionalUserDialog.saving"
            @click="addAdditionalUser"
          >
            {{
              additionalUserDialog.mode === 'edit'
                ? 'Guardar cambios'
                : 'Guardar usuario'
            }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog max-width="420" persistent v-model="resetDialog.show">
      <v-card class="modal-card">
        <div class="modal-header">
          <h3 class="modal-header__title">Reset de contraseña por defecto</h3>
          <p class="modal-header__subtitle">
            Esta acción aplicará la contraseña por defecto al usuario vinculado
            a este distribuidor.
          </p>
        </div>
        <v-card-text class="modal-body">
          <div class="info-note info-note--warning">
            <v-icon small class="info-note__icon">warning</v-icon>
            <div>
              <p>
                Asegúrate de comunicar la nueva contraseña al usuario de forma
                segura.
              </p>
            </div>
          </div>
          <div
            v-if="resetDialog.result"
            class="reset-result reset-result--compact"
          >
            <p class="reset-result__title">Contraseña restablecida</p>
            <p class="reset-result__meta">{{ resetDialog.result.email }}</p>
            <p class="reset-result__password">
              <span>Password aplicada</span>
              <strong>{{ resetDialog.result.password || '***' }}</strong>
            </p>
          </div>
        </v-card-text>
        <div class="modal-footer">
          <v-btn class="btn-cancel" flat small @click="closeResetPasswordDialog"
            >Cerrar</v-btn
          >
          <v-btn
            class="btn-save"
            small
            :loading="resetDialog.saving"
            @click="resetPasswordToDefault"
          >
            <v-icon left small>lock_reset</v-icon>Ejecutar reset
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from '@/api/index'
import message from './../dialog/Message'

const DEFAULT_CENTER = { lat: 8.9824, lng: -79.5199 } // Ciudad de Panamá

export default {
  components: { message },
  props: {
    form: {
      type: Object,
      default() {
        return {
          name: '',
          phone: '',
          address: '',
          latitude: '',
          longitude: '',
          emails: [],
          additionalUsers: [],
          contact: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
          },
          laboratories: [],
          accId: '',
        }
      },
    },
  },
  data() {
    return {
      saving: false,
      showPassword: false,
      showConfirmPassword: false,
      showAdditionalPassword: false,
      showAdditionalConfirmPassword: false,
      confirmPassword: '',
      passwordErrorText: '',
      enableEmails: false,
      mapCenter: { ...DEFAULT_CENTER },
      mapZoom: 12,
      laboratories: [],
      roles: [],
      distributorRoleId: 6,
      contactUserId: null,
      initialAdditionalUsers: [],
      additionalUserDialog: {
        show: false,
        saving: false,
        mode: 'create',
        editingIndex: -1,
        form: {
          id: null,
          firstName: '',
          lastName: '',
          email: '',
          roleId: '',
          password: '',
          confirmPassword: '',
        },
        errors: {},
      },
      resetDialog: { show: false, saving: false, result: null },
      dialog: { show: false },
    }
  },
  created() {
    if (!Array.isArray(this.form.additionalUsers)) {
      this.form = Object.assign(this.form, { additionalUsers: [] })
    }
    this.loadRoles()
    this.$store
      .dispatch('laboratories/getAll', { roleId: 3 })
      .then(response => {
        const data = response.items
        for (let index = 0; index < data.length; index += 1) {
          this.laboratories.push({ name: data[index].name, id: data[index].id })
        }
      })

    if ('id' in this.$route.params) {
      this.$store
        .dispatch('distributors/getById', { id: this.$route.params.id })
        .then(response => {
          if (response) {
            const emails = response.emails ? response.emails.split(',') : []
            const laboratories = response.laboratories.reduce((acc, lab) => {
              acc.push(lab.id)
              return acc
            }, [])
            this.form = Object.assign(this.form, {
              id: response.id,
              name: response.name,
              phone: response.phone,
              address: response.address,
              latitude:
                response.latitude != null && response.latitude !== ''
                  ? Number(response.latitude)
                  : '',
              longitude:
                response.longitude != null && response.longitude !== ''
                  ? Number(response.longitude)
                  : '',
              emails: emails,
              laboratories: laboratories,
              additionalUsers: [],
              contact: {
                firstName: response.contact.firstName,
                lastName: response.contact.lastName,
                email: response.contact.email,
              },
              accId: response.accounting !== null ? response.accounting.id : '',
            })
            this.contactUserId = response.contact.id || null
            this.loadAdditionalUsers(response.id)
            if (emails.length) this.enableEmails = true
            this.$nextTick(() => this.applyLocationFromForm())
          } else {
            this.$router.push('/distribuidores/listado')
          }
        })
    }
  },
  computed: {
    hasLocation() {
      return (
        this.form.latitude !== '' &&
        this.form.latitude != null &&
        this.form.longitude !== '' &&
        this.form.longitude != null
      )
    },
    markerPosition() {
      if (!this.hasLocation) return null
      return {
        lat: Number(this.form.latitude),
        lng: Number(this.form.longitude),
      }
    },
  },
  methods: {
    setCoords(lat, lng, options = {}) {
      this.form.latitude = lat
      this.form.longitude = lng
      this.mapCenter = { lat, lng }
      if (options.zoom) this.mapZoom = options.zoom
    },
    applyLocationFromForm() {
      if (this.hasLocation) {
        this.mapCenter = {
          lat: Number(this.form.latitude),
          lng: Number(this.form.longitude),
        }
        this.mapZoom = 16
      }
    },
    onPlaceChanged(place) {
      if (!place || !place.geometry) return
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      this.setCoords(lat, lng, { zoom: 17 })
      if (!this.form.address) {
        this.form.address = place.formatted_address || place.name || ''
      }
    },
    onMapClick(event) {
      if (!event || !event.latLng) return
      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      this.setCoords(lat, lng)
      this.maybeFillAddress(lat, lng)
    },
    async onMarkerDragEnd(event) {
      if (!event || !event.latLng) return
      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      this.setCoords(lat, lng)
      this.maybeFillAddress(lat, lng)
    },
    async maybeFillAddress(lat, lng) {
      if (this.form.address) return
      const address = await this.reverseGeocode(lat, lng)
      if (address && !this.form.address) this.form.address = address
    },
    async reverseGeocode(lat, lng) {
      try {
        const google = await this.$gmapApiPromiseLazy()
        const geocoder = new google.maps.Geocoder()
        return await new Promise(resolve => {
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              resolve(results[0].formatted_address)
            } else {
              resolve('')
            }
          })
        })
      } catch (error) {
        return ''
      }
    },
    clearLocation() {
      this.form.latitude = ''
      this.form.longitude = ''
    },
    loadRoles() {
      return this.$store.dispatch('users/getRoles').then(response => {
        const items = Array.isArray(response)
          ? response
          : Array.isArray(response.data)
          ? response.data
          : []
        this.roles = items.map(role => ({
          id: role.id,
          text: role.name,
        }))
        return this.roles
      })
    },
    normalizeAdditionalUsers(response) {
      const source = Array.isArray(response.additionalUsers)
        ? response.additionalUsers
        : Array.isArray(response.users)
        ? response.users
        : []
      const contactId =
        (response.contact && response.contact.id) || this.contactUserId
      return source
        .filter(user => !contactId || user.id !== contactId)
        .map(user => ({
          id: user.id,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          roleId: user.roleId || (user.role && user.role.id) || '',
          roleName:
            typeof user.role === 'string'
              ? user.role
              : (user.role && user.role.name) || user.roleName || '',
          role: user.role || null,
          password: '',
        }))
    },
    async loadAdditionalUsers(distributorId) {
      try {
        const usersResponse = await Api.Distributor.getUsers({
          id: distributorId,
        })
        const normalized = this.normalizeAdditionalUsers(usersResponse || {})
        this.form.additionalUsers = normalized
        this.initialAdditionalUsers = normalized.map(user =>
          Object.assign({}, user)
        )
      } catch (error) {
        this.form.additionalUsers = []
        this.initialAdditionalUsers = []
      }
    },
    getRoleName(roleId) {
      const match = this.roles.find(role => role.id === roleId)
      return match ? match.text : 'Sin rol'
    },
    getAdditionalUserRole(user) {
      return user.roleName || this.getRoleName(user.roleId) || 'Distribuidor'
    },
    getAdditionalUserName(user) {
      return (
        `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Sin nombre'
      )
    },
    openAdditionalUserDialog(user = null, index = -1) {
      this.additionalUserDialog.show = true
      this.additionalUserDialog.saving = false
      this.additionalUserDialog.errors = {}
      this.additionalUserDialog.mode = user ? 'edit' : 'create'
      this.additionalUserDialog.editingIndex = index
      this.additionalUserDialog.form = user
        ? {
            id: user.id || null,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            roleId: user.roleId || this.distributorRoleId,
            password: '',
            confirmPassword: '',
          }
        : {
            id: null,
            firstName: '',
            lastName: '',
            email: '',
            roleId: this.distributorRoleId,
            password: '',
            confirmPassword: '',
          }
      this.showAdditionalPassword = false
      this.showAdditionalConfirmPassword = false
    },
    closeAdditionalUserDialog() {
      this.additionalUserDialog.show = false
      this.additionalUserDialog.saving = false
      this.additionalUserDialog.mode = 'create'
      this.additionalUserDialog.editingIndex = -1
      this.additionalUserDialog.errors = {}
    },
    validateAdditionalUser() {
      const form = this.additionalUserDialog.form
      const errors = {}
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!form.firstName.trim()) errors.firstName = 'El nombre es requerido.'
      if (!form.lastName.trim()) errors.lastName = 'El apellido es requerido.'
      if (!form.email.trim()) {
        errors.email = 'El correo es requerido.'
      } else if (!emailPattern.test(form.email.trim())) {
        errors.email = 'Ingrese un correo válido.'
      } else {
        const duplicate =
          (this.form.additionalUsers || []).some(
            user =>
              (user.email || '').toLowerCase() ===
                form.email.trim().toLowerCase() && user.id !== form.id
          ) ||
          form.email.trim().toLowerCase() ===
            (this.form.contact.email || '').toLowerCase()
        if (duplicate) errors.email = 'Ya existe un usuario con este correo.'
      }
      const requiresPassword = this.additionalUserDialog.mode !== 'edit'
      const hasAnyPassword = !!(form.password || form.confirmPassword)
      if (requiresPassword && !form.password)
        errors.password = 'La contraseña es requerida.'
      if ((requiresPassword || hasAnyPassword) && !form.confirmPassword) {
        errors.confirmPassword = 'Debe confirmar la contraseña.'
      } else if (hasAnyPassword && form.password !== form.confirmPassword) {
        errors.confirmPassword = 'Las contraseñas no coinciden.'
      }
      this.additionalUserDialog.errors = errors
      return !Object.keys(errors).length
    },
    async addAdditionalUser() {
      if (!this.validateAdditionalUser()) return
      if (!this.form.id) return

      const form = this.additionalUserDialog.form
      const userDraft = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        roleId: this.distributorRoleId,
        roleName: 'Distribuidor',
        password: form.password,
      }

      this.additionalUserDialog.saving = true
      try {
        const payload = this.buildAdditionalUserPayload(
          Object.assign({}, userDraft, {
            id: this.additionalUserDialog.form.id,
          }),
          this.form.id
        )

        if (this.additionalUserDialog.mode === 'edit' && payload.id) {
          const updatePayload = {
            firstName: payload.firstName,
            lastName: payload.lastName,
            roleId: payload.roleId,
            email: payload.email,
            password: payload.password || '',
            enabled: payload.enabled === true || payload.enabled === 1,
          }
          const updateResponse = await Api.User.updateById(
            payload.id,
            updatePayload
          )
          if (!updateResponse || updateResponse.status !== 200) {
            throw new Error(
              (updateResponse &&
                updateResponse.data &&
                updateResponse.data.error) ||
                'No se pudo editar el usuario adicional.'
            )
          }

          const idx = this.additionalUserDialog.editingIndex
          if (idx > -1) {
            const merged = Object.assign({}, this.form.additionalUsers[idx], {
              firstName: updatePayload.firstName,
              lastName: updatePayload.lastName,
              email: updatePayload.email,
              roleId: updatePayload.roleId,
              roleName: 'Distribuidor',
              password: '',
              _dirty: false,
            })
            this.form.additionalUsers.splice(idx, 1, merged)
            const prevIdx = this.initialAdditionalUsers.findIndex(
              u => u.id === merged.id
            )
            if (prevIdx > -1)
              this.initialAdditionalUsers.splice(
                prevIdx,
                1,
                Object.assign({}, merged)
              )
          }
        } else {
          const createPayload = {
            firstName: payload.firstName,
            lastName: payload.lastName,
            email: payload.email,
            password: payload.password,
            enabled: payload.enabled === true || payload.enabled === 1,
          }
          const response = await Api.Distributor.addAdditionalUser(
            this.form.id,
            createPayload
          )
          if (!response || (response.status && response.status >= 400)) {
            throw new Error(
              (response && response.data && response.data.error) ||
                'No se pudo guardar el usuario adicional.'
            )
          }

          const savedUser =
            (response.data && (response.data.user || response.data)) ||
            response.data ||
            {}
          const normalizedUser = Object.assign({}, userDraft, {
            id: savedUser.id || null,
            roleId: savedUser.roleId || payload.roleId,
            enabled: Object.prototype.hasOwnProperty.call(savedUser, 'enabled')
              ? savedUser.enabled
              : 1,
            password: '',
            _dirty: false,
          })

          this.form.additionalUsers.push(normalizedUser)
          this.initialAdditionalUsers.push(Object.assign({}, normalizedUser))
        }

        this.closeAdditionalUserDialog()
      } catch (error) {
        this.additionalUserDialog.saving = false
        this.dialog = {
          message:
            error && error.message
              ? error.message
              : 'No se pudo guardar el usuario adicional.',
          color: 'error',
          show: true,
        }
      }
    },
    async removeAdditionalUser(user, index) {
      if (!user || !user.id) {
        this.form.additionalUsers.splice(index, 1)
        return
      }

      try {
        const response = await Api.User.deleteById(user.id)
        if (!response || response.status !== 200) {
          throw new Error(
            (response && response.data && response.data.error) ||
              'No se pudo eliminar el usuario adicional.'
          )
        }

        this.form.additionalUsers.splice(index, 1)
        this.initialAdditionalUsers = this.initialAdditionalUsers.filter(
          prev => prev.id !== user.id
        )
      } catch (error) {
        this.dialog = {
          message:
            error && error.message
              ? error.message
              : 'No se pudo eliminar el usuario adicional.',
          color: 'error',
          show: true,
        }
      }
    },
    buildAdditionalUserPayload(user, distributorId) {
      return {
        id: user.id,
        firstName: (user.firstName || '').trim(),
        lastName: (user.lastName || '').trim(),
        email: (user.email || '').trim(),
        password: user.password || '',
        roleId: user.roleId || this.distributorRoleId,
        distributorId,
        distId: distributorId,
        enabled: Object.prototype.hasOwnProperty.call(user, 'enabled')
          ? user.enabled
          : 1,
      }
    },
    async syncAdditionalUsers(distributorId) {
      const currentUsers = Array.isArray(this.form.additionalUsers)
        ? this.form.additionalUsers
        : []
      const previousUsers = Array.isArray(this.initialAdditionalUsers)
        ? this.initialAdditionalUsers
        : []

      const removedUsers = previousUsers.filter(previousUser => {
        if (!previousUser.id) return false
        return !currentUsers.some(
          currentUser => currentUser.id === previousUser.id
        )
      })

      for (let index = 0; index < currentUsers.length; index += 1) {
        if (currentUsers[index].id && !currentUsers[index]._dirty) continue
        const payload = this.buildAdditionalUserPayload(
          currentUsers[index],
          distributorId
        )
        const response = await this.$store.dispatch('users/save', payload)
        if (!response || response.status !== 200) {
          throw new Error(
            (response && response.data && response.data.error) ||
              'No se pudo guardar un usuario adicional.'
          )
        }

        const savedUser =
          (response.data && (response.data.user || response.data)) ||
          response.data ||
          {}
        currentUsers[index] = Object.assign({}, currentUsers[index], {
          id: savedUser.id || currentUsers[index].id,
          roleId: savedUser.roleId || payload.roleId,
          roleName: currentUsers[index].roleName || 'Distribuidor',
          password: '',
          enabled: Object.prototype.hasOwnProperty.call(savedUser, 'enabled')
            ? savedUser.enabled
            : 1,
          _dirty: false,
        })
      }

      for (let index = 0; index < removedUsers.length; index += 1) {
        const removedUser = removedUsers[index]
        const response = await Api.User.deleteById(removedUser.id)
        if (!response || response.status !== 200) {
          throw new Error(
            (response && response.data && response.data.error) ||
              'No se pudo eliminar un usuario adicional.'
          )
        }
      }

      this.initialAdditionalUsers = currentUsers.map(user =>
        Object.assign({}, user)
      )
    },
    submit() {
      this.passwordErrorText = ''
      if (!this.form.id) {
        if (!this.confirmPassword) {
          this.passwordErrorText = 'Debes confirmar la contraseña.'
          return
        }
        if (this.form.contact.password !== this.confirmPassword) {
          this.passwordErrorText = 'Las contraseñas no coinciden.'
          return
        }
      }
      this.$validator.validateAll().then(result => {
        if (!result) return
        this.saving = true
        const distributorPayload = Object.assign({}, this.form)
        delete distributorPayload.additionalUsers
        this.$store
          .dispatch('distributors/save', distributorPayload)
          .then(response => {
            if (response.status !== 200) {
              this.saving = false
              this.dialog = {
                message: response.data.error,
                color: 'error',
                show: true,
              }
              return
            }

            const savedDistributorId =
              this.form.id || (response.data && response.data.id)
            const syncPromise =
              this.form.id && savedDistributorId
                ? this.syncAdditionalUsers(savedDistributorId)
                : Promise.resolve()

            syncPromise
              .then(() => {
                this.saving = false
                if (this.form.id) {
                  this.dialog = {
                    message: 'Los datos fueron actualizados exitosamente',
                    color: 'primary',
                    show: true,
                  }
                } else {
                  this.$router.push('/distribuidores/listado')
                }
              })
              .catch(error => {
                this.saving = false
                this.dialog = {
                  message:
                    error && error.message
                      ? error.message
                      : 'No se pudieron sincronizar los usuarios adicionales.',
                  color: 'error',
                  show: true,
                }
              })
          })
          .catch(() => {
            this.saving = false
            this.dialog = {
              message: 'No se pudo conectar al servidor. Intenta más tarde.',
              color: 'error',
              show: true,
            }
          })
      })
    },
    openResetPasswordDialog() {
      this.resetDialog.show = true
      this.resetDialog.result = null
    },
    closeResetPasswordDialog() {
      this.resetDialog.show = false
      this.resetDialog.saving = false
      this.resetDialog.result = null
    },
    async resetPasswordToDefault() {
      if (!this.contactUserId) return
      this.resetDialog.saving = true
      try {
        const response = await Api.User.resetPassword(this.contactUserId, {
          useDefaultPassword: true,
        })
        if (
          response &&
          typeof response.status === 'number' &&
          response.status >= 400
        ) {
          throw new Error('Error de reset')
        }
        const payload = response.data || response
        this.resetDialog.result = {
          email: payload.email,
          password: payload.password,
          usedDefaultPassword: payload.usedDefaultPassword,
        }
      } catch (error) {
        this.dialog = {
          message: 'No se pudo restablecer la contraseña por defecto.',
          color: 'error',
          show: true,
        }
        this.closeResetPasswordDialog()
      } finally {
        this.resetDialog.saving = false
      }
    },
  },
}
</script>

<style scoped>
.dist-form-page {
  padding: 24px 28px 40px;
  color: #172133;
  background: #f9fafb;
  min-height: 100%;
}

.form-hero {
  margin-bottom: 24px;
}

.form-crumb {
  margin: 0 0 6px;
  font-size: 12.5px;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.form-title {
  margin: 0 0 6px;
  font-size: 26px;
  font-weight: 700;
  color: #172133;
  letter-spacing: -0.01em;
}

.form-subtitle {
  margin: 0;
  font-size: 13.5px;
  color: #64748b;
  max-width: 720px;
}

.form-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 14px rgba(15, 23, 42, 0.05);
}

.form-section {
  margin-bottom: 0;
}

.form-section__title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 700;
  color: #172133;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  position: relative;
  padding-bottom: 10px;
}

.form-section__title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 32px;
  height: 2px;
  background: #e5252a;
  border-radius: 1px;
}

.form-section__badge {
  font-size: 11px;
  font-weight: 600;
  color: #254f87;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 6px;
}

.form-section__help {
  margin: 0 0 16px;
  font-size: 12.5px;
  color: #64748b;
}

.form-divider {
  height: 1px;
  background: #eef2f7;
  margin: 24px 0;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 18px;
  margin-top: 16px;
}

.form-field--full {
  grid-column: 1 / -1;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: 0.01em;
}

.form-required {
  color: #e5252a;
  font-weight: 700;
}

.form-label-opt {
  font-size: 11px;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 2px;
}

.form-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
}

.form-hint__icon {
  color: #94a3b8 !important;
  font-size: 13px !important;
}

.form-error-text {
  margin: 6px 2px 0;
  color: #e5252a;
  font-size: 11.5px;
  font-weight: 600;
}

.map-search-wrap {
  position: relative;
  margin-bottom: 10px;
}

.map-search-wrap__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8 !important;
  z-index: 1;
}

.map-search-input {
  width: 100%;
  height: 44px;
  padding: 0 14px 0 38px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  color: #172133;
  font-size: 14px;
  outline: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.map-search-input:hover {
  border-color: #9ca3af;
}

.map-search-input:focus {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12),
    0 1px 2px rgba(15, 23, 42, 0.04);
}

.map-search-input::placeholder {
  color: #94a3b8;
}

.dist-map {
  height: 320px;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  overflow: hidden;
  z-index: 0;
}

.map-coords {
  display: flex;
  align-items: center;
  gap: 4px;
}

.map-clear {
  color: #e5252a;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;
  margin-left: 6px;
}

.form-toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 6px 0;
}

.form-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.form-toggle-text {
  flex: 1;
  padding-top: 6px;
}

.form-toggle-label {
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: #172133;
}

.form-toggle-help {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: #64748b;
}

.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #eef2f7;
}

.btn-cancel {
  border: 1px solid #e2e8f0 !important;
  background: transparent !important;
  color: #475569 !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  height: 42px !important;
  padding: 0 18px !important;
  box-shadow: none !important;
}

.btn-cancel:hover {
  background: #f8fafc !important;
  color: #172133 !important;
  border-color: #cbd5e1 !important;
}

.btn-save {
  background: #e5252a !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  height: 42px !important;
  padding: 0 22px !important;
  box-shadow: 0 1px 2px rgba(229, 37, 42, 0.12),
    0 4px 12px rgba(229, 37, 42, 0.2) !important;
  transition: all 0.15s ease !important;
}

.btn-save:hover {
  background: #c91e23 !important;
  box-shadow: 0 2px 4px rgba(229, 37, 42, 0.18),
    0 6px 16px rgba(229, 37, 42, 0.28) !important;
  transform: translateY(-1px);
}

.btn-secondary {
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  color: #254f87 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.btn-secondary:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.form-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fafbfd;
}

.form-action-row__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #172133;
}

.form-action-row__help {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: #64748b;
}

.additional-users__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.additional-users__title {
  margin-bottom: 6px;
}

.additional-users__help {
  margin-bottom: 0;
}

.additional-users__btn {
  background: linear-gradient(135deg, #6d28d9, #7c3aed) !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.additional-users__table-wrap {
  margin-top: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}

.additional-users__table {
  width: 100%;
  border-collapse: collapse;
}

.additional-users__table th,
.additional-users__table td {
  padding: 14px 16px;
  text-align: left;
  font-size: 13px;
  border-bottom: 1px solid #eef2f7;
}

.additional-users__table th {
  font-weight: 700;
  color: #172133;
  background: #fcfdff;
}

.additional-users__table td {
  color: #475569;
}

.additional-users__table tbody tr:last-child td {
  border-bottom: 0;
}

.additional-users__remove {
  color: #64748b !important;
}

.additional-users__edit {
  color: #254f87 !important;
}

.additional-users__cards {
  display: none;
}

.additional-users__card {
  border-top: 1px solid #eef2f7;
  padding: 14px 16px;
  background: #ffffff;
}

.additional-users__card-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.additional-users__card-row:last-child {
  margin-bottom: 0;
}

.additional-users__card-label {
  color: #64748b;
  font-size: 11.5px;
  font-weight: 600;
}

.additional-users__card-value {
  color: #172133;
  font-size: 12.5px;
  text-align: right;
  word-break: break-word;
}

.additional-users__card-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.additional-users__remove-text {
  color: #64748b !important;
  text-transform: none !important;
}

.additional-users__edit-text {
  color: #254f87 !important;
  text-transform: none !important;
}

.additional-users__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 28px 20px;
  color: #64748b;
}

.additional-users__empty-icon {
  font-size: 44px !important;
  color: #94a3b8 !important;
}

.additional-users__empty-title {
  margin: 0 0 3px;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}

.additional-users__empty-text {
  margin: 0;
  font-size: 12px;
}

.form-grid--modal {
  margin-top: 0;
}

.native-form-input {
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  color: #172133;
  font-size: 14px;
  outline: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.native-form-input:hover {
  border-color: #9ca3af;
}

.native-form-input:focus {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12),
    0 1px 2px rgba(15, 23, 42, 0.04);
}

.native-form-input::placeholder {
  color: #94a3b8;
}

.native-form-input--readonly {
  background: #f8fafc;
  color: #64748b;
}

.native-form-input--password {
  display: flex;
  align-items: center;
  padding: 0 10px 0 14px;
}

.native-form-input__control {
  width: 100%;
  height: 42px;
  border: 0;
  outline: none;
  background: transparent;
  color: #172133;
  font-size: 14px;
}

.native-form-input__control::placeholder {
  color: #94a3b8;
}

.native-form-input__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
}

.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.modal-header {
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.modal-header__title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: #172133;
}

.modal-header__subtitle {
  margin: 0;
  font-size: 12.5px;
  color: #64748b;
}

.modal-body {
  padding: 14px 20px !important;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #eef2f7;
}

.info-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 14px;
}

.info-note--warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.info-note__icon {
  color: #d97706 !important;
  margin-top: 1px;
}

.reset-result {
  background: #f1f5f9;
  border-radius: 8px;
  padding: 12px 14px;
}

.reset-result--compact {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reset-result__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #172133;
}

.reset-result__meta {
  margin: 0;
  font-size: 11.5px;
  color: #64748b;
}

.reset-result__password {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 4px 0 0;
  font-size: 12px;
}

.reset-result__password span {
  color: #64748b;
}

.reset-result__password strong {
  color: #172133;
  word-break: break-all;
}

@media (max-width: 720px) {
  .dist-form-page {
    padding: 18px 14px 28px;
  }
  .form-card {
    padding: 18px 16px;
  }
  .form-title {
    font-size: 22px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .additional-users__header,
  .form-action-row {
    flex-direction: column;
    align-items: stretch;
  }
  .additional-users__btn {
    width: 100%;
    justify-content: center;
  }
  .additional-users__table {
    display: none;
  }
  .additional-users__cards {
    display: block;
  }
  .additional-users__table-wrap {
    overflow: visible;
  }
  .form-footer {
    flex-direction: column-reverse;
  }
  .btn-cancel,
  .btn-save {
    width: 100%;
    justify-content: center;
  }
}
</style>

<style>
.dist-form-page
  .form-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot,
.dist-form-page
  .form-input.v-textarea.v-text-field--solo
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
  min-height: 44px !important;
  padding: 0 14px !important;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.dist-form-page
  .form-input.v-text-field.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot,
.dist-form-page
  .form-input.v-textarea.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot {
  border-color: #9ca3af !important;
}

.dist-form-page
  .form-input.v-text-field.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot,
.dist-form-page
  .form-input.v-textarea.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot {
  border-color: #254f87 !important;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12),
    0 1px 2px rgba(15, 23, 42, 0.04) !important;
}

.dist-form-page .form-input.error--text .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
}

.dist-form-page .form-input input,
.dist-form-page .form-input textarea {
  color: #172133 !important;
  font-size: 14px !important;
  caret-color: #172133 !important;
}

.dist-form-page .form-input input::placeholder,
.dist-form-page .form-input textarea::placeholder {
  color: #94a3b8 !important;
  opacity: 1;
}

.dist-form-page .form-input .v-input__icon .v-icon {
  color: #94a3b8 !important;
  font-size: 19px !important;
}

/* Switch estilo iOS */
.dist-form-page .form-switch .v-input--switch__track {
  background: #e5e7eb !important;
  opacity: 1 !important;
  height: 22px !important;
  width: 40px !important;
  border-radius: 11px !important;
  top: calc(50% - 11px) !important;
  transition: background-color 0.25s ease;
}

.dist-form-page .form-switch.v-input--is-label-active .v-input--switch__track {
  background: #e5252a !important;
  opacity: 1 !important;
}

.dist-form-page .form-switch .v-input--switch__thumb {
  background: #ffffff !important;
  width: 18px !important;
  height: 18px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.06) !important;
  top: calc(50% - 9px) !important;
}
</style>
